
import React, { useState } from 'react';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'message' | 'schedule'>('message');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create FormData for Netlify form submission
      const formDataToSend = new FormData();
      formDataToSend.append('form-name', 'contact-demo-form');
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Ready to transform your business? Send us a message or schedule a consultation 
            to discuss how we can help you achieve your goals.
          </p>
          <div className="mt-8 bg-teal-500 w-24 h-1 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 text-white h-full">
              <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Email Us</h4>
                    <a href="mailto:admin@antriva.co.in" className="text-teal-100 hover:text-white transition-colors">
                      admin@antriva.co.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Call Us</h4>
                    <a href="tel:+917795709574" className="text-teal-100 hover:text-white transition-colors">
                      +91 7795709574
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Office Hours</h4>
                    <p className="text-teal-100 text-sm">Monday - Friday: 9 AM - 6 PM</p>
                    <p className="text-teal-100 text-sm">Weekend: Closed</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Quick Scheduling</h4>
                    <p className="text-teal-100 text-sm">Book a meeting instantly with our calendar</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/20">
                <h4 className="font-semibold mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors">
                    <i className="ri-linkedin-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors">
                    <i className="ri-twitter-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors">
                    <i className="ri-github-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form & Calendly */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-2xl p-8">
              {/* Tab Switcher */}
              <div className="flex gap-2 mb-8 bg-white p-1 rounded-full w-fit mx-auto">
                <button
                  onClick={() => setActiveTab('message')}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'message'
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-mail-line mr-2"></i>
                  Send Message
                </button>
                <button
                  onClick={() => setActiveTab('schedule')}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'schedule'
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Meeting
                </button>
              </div>

              {/* Message Form */}
              {activeTab === 'message' && (
                <div className="animate-fadeIn">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Send Us A Message
                  </h3>

                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-6" 
                    name="contact-demo-form"
                    method="POST"
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                  >
                    {/* Hidden field for Netlify */}
                    <input type="hidden" name="form-name" value="contact-demo-form" />
                    
                    {/* Honeypot field for spam protection */}
                    <div style={{ display: 'none' }}>
                      <label>
                        Don't fill this out if you're human: <input name="bot-field" />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                          placeholder="Your Company Ltd."
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        Service of Interest *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">Select a service</option>
                        <option value="ai-agents">AI Agents & Automation</option>
                        <option value="observability">Observability</option>
                        <option value="data-engineering">Data Engineering & BI</option>
                        <option value="mvp-development">Rapid MVP Development</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 resize-none"
                        placeholder="Tell us about your project or requirements..."
                      />
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.message.length}/500 characters
                      </div>
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                        Thank you! Your message has been sent successfully. We'll get back to you soon.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                        Sorry, there was an error sending your message. Please try again or contact us directly.
                      </div>
                    )}

                    <p className="text-xs text-gray-500 text-center">
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </form>
                </div>
              )}

              {/* Calendly Embed */}
              {activeTab === 'schedule' && (
                <div className="animate-fadeIn">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Schedule A Consultation
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Choose a convenient time for a 30-minute consultation with our team. 
                    We'll discuss your project requirements and how we can help.
                  </p>
                  
                  {/* Calendly Inline Widget */}
                  <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                    <iframe
                      src="https://calendly.com/admin-antriva/30min"
                      width="100%"
                      height="700"
                      frameBorder="0"
                      title="Schedule a meeting"
                      className="rounded-lg"
                    ></iframe>
                  </div>

                  <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-start space-x-3">
                      <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-teal-900 mb-1">What to expect:</h4>
                        <ul className="text-sm text-teal-800 space-y-1">
                          <li>• 30-minute video consultation</li>
                          <li>• Discussion of your project requirements</li>
                          <li>• Initial recommendations and next steps</li>
                          <li>• Q&A session about our services</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
